import type { Buffer } from "../model/buffer";
import { lineLength } from "../model/buffer";
import type { Cursor } from "../model/cursor";
import type { Range } from "../model/range";
import { stepBackward, stepForward } from "../motions/word-class";

function charAt(buffer: Buffer, pos: Cursor): string {
  if (pos.col >= lineLength(buffer, pos.line)) {
    return "";
  }
  const line = buffer.lines[pos.line] ?? "";
  return line[pos.col] ?? "";
}

function comparePositions(a: Cursor, b: Cursor): number {
  return a.line - b.line || a.col - b.col;
}

interface EnclosingPair {
  readonly openPos: Cursor;
  readonly closePos: Cursor;
}

/** カーソルを内側に含む、直近の未対応の開き括弧〜対応する閉じ括弧を探す（複数行対応）。 */
function findEnclosingPair(
  buffer: Buffer,
  cursor: Cursor,
  open: string,
  close: string,
): EnclosingPair | null {
  let depth = 0;
  let pos: Cursor | null = cursor;
  let openPos: Cursor | null = null;

  while (pos !== null) {
    const ch = charAt(buffer, pos);
    const isCursorPos = comparePositions(pos, cursor) === 0;
    if (ch === close && !isCursorPos) {
      depth += 1;
    } else if (ch === open) {
      if (depth === 0) {
        openPos = pos;
        break;
      }
      depth -= 1;
    }
    pos = stepBackward(buffer, pos);
  }

  if (openPos === null) {
    return null;
  }

  depth = 0;
  pos = stepForward(buffer, openPos);
  let closePos: Cursor | null = null;
  while (pos !== null) {
    const ch = charAt(buffer, pos);
    if (ch === open) {
      depth += 1;
    } else if (ch === close) {
      if (depth === 0) {
        closePos = pos;
        break;
      }
      depth -= 1;
    }
    pos = stepForward(buffer, pos);
  }

  if (closePos === null) {
    return null;
  }
  return { openPos, closePos };
}

/** `i(` `a(` `i{` `a{` 等: カーソルを囲む括弧の対を対象にする。 */
export function bracketTextObject(
  buffer: Buffer,
  cursor: Cursor,
  open: string,
  close: string,
  around: boolean,
): Range | null {
  const pair = findEnclosingPair(buffer, cursor, open, close);
  if (!pair) {
    return null;
  }

  if (around) {
    return { start: pair.openPos, end: pair.closePos, kind: "inclusive" };
  }

  const innerStart = stepForward(buffer, pair.openPos);
  if (innerStart === null) {
    return { start: pair.openPos, end: pair.openPos, kind: "exclusive" };
  }
  return { start: innerStart, end: pair.closePos, kind: "exclusive" };
}
